import { customFetch } from '~/utils/customFetch';

const BASE_PATH = '/authenticated/client';

export type EntityFilterType = 'clients' | 'products';

export interface Entity {
  id: number;
  name: string | null;
  status?: string | null;
  active?: string | null;
}

/** Hydrated entity with activity flag - client or product */
export type HydratedEntity = Entity & { hasActivityInRange?: boolean };

export interface HydratedResponse {
  entities: Array<HydratedEntity>;
}

export interface SuggestionsResponse {
  entities: Entity[];
}

export interface SearchResponse {
  entities: Entity[];
}

/** Convert YYYYMMDD to yyyy-mm-dd for API */
function toApiDate(yyyymmdd: string): string {
  if (yyyymmdd.length !== 8) return yyyymmdd;
  return `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`;
}

function buildHydrateParams(
  ids: number[],
  dateFrom: string,
  dateTo: string,
  clientIds?: number[],
): URLSearchParams {
  const params = new URLSearchParams();
  ids.forEach((id) => params.append('id', String(id)));
  params.set('date_from', toApiDate(dateFrom));
  params.set('date_to', toApiDate(dateTo));
  if (clientIds?.length) {
    clientIds.forEach((cid) => params.append('client_id', String(cid)));
  }
  return params;
}

function buildSuggestionsParams(
  dateFrom: string,
  dateTo: string,
  limit?: number,
  clientIds?: number[],
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('date_from', toApiDate(dateFrom));
  params.set('date_to', toApiDate(dateTo));
  if (limit != null) params.set('limit', String(limit));
  if (clientIds?.length) {
    clientIds.forEach((cid) => params.append('client_id', String(cid)));
  }
  return params;
}

function buildSearchParams(query: string, limit?: number, clientIds?: number[]): URLSearchParams {
  const params = new URLSearchParams();
  params.set('query', query);
  if (limit != null) params.set('limit', String(limit));
  if (clientIds?.length) {
    clientIds.forEach((cid) => params.append('client_id', String(cid)));
  }
  return params;
}

/**
 * GET …/entities/{clients|products}/hydrate
 *
 * @remarks
 * Invoked via React Query from `useEntityFilterHydrateQuery` (used by
 * `SelectedSection` in the analytics entity filter drawer). Prefetched on analytics
 * stats builder / saved-view routes when `cid` / `pid` URL params are present
 * (`prefetchAnalyticsEntityFilterQueries` in `useEntityFilterQueries`).
 */
export async function hydrateEntities(
  accountId: number,
  ids: number[],
  dateFrom: string,
  entityType: EntityFilterType,
  dateTo: string,
): Promise<HydratedResponse> {
  if (ids.length === 0) {
    return { entities: [] };
  }
  const { data } = await customFetch.get<HydratedResponse>(
    `${BASE_PATH}/${accountId}/entities/${entityType}/hydrate`,
    {
      useNodeEndpoint: true,
      params: buildHydrateParams(ids, dateFrom, dateTo),
    },
  );
  return data;
}

/**
 * GET …/entities/{clients|products}/suggestions
 *
 * @remarks
 * Invoked via `useEntityFilterSuggestionsQuery` (`SuggestedSection` in the analytics
 * entity filter drawer). Prefetched on analytics stats builder and saved-view routes
 * (`prefetchAnalyticsEntityFilterQueries` in `useEntityFilterQueries`).
 */
export async function getEntitiesSuggestions(
  accountId: number,
  dateFrom: string,
  dateTo: string,
  entityType: EntityFilterType,
  limit = 50,
  clientIds?: number[],
): Promise<SuggestionsResponse> {
  const { data } = await customFetch.get<SuggestionsResponse>(
    `${BASE_PATH}/${accountId}/entities/${entityType}/suggestions`,
    {
      useNodeEndpoint: true,
      params: buildSuggestionsParams(dateFrom, dateTo, limit, clientIds),
    },
  );
  return data;
}

/**
 * GET …/entities/{clients|products}/search
 *
 * @remarks
 * Invoked on demand when the user types in the filter drawer search field
 * (`useEntityFilterSearchQuery` / `SearchSection`). Not prefetched (search is
 * query-specific).
 */
export async function searchEntities(
  accountId: number,
  query: string,
  entityType: EntityFilterType,
  limit = 20,
): Promise<SearchResponse> {
  if (!query.trim()) {
    return { entities: [] };
  }
  const { data } = await customFetch.get<SearchResponse>(
    `${BASE_PATH}/${accountId}/entities/${entityType}/search`,
    {
      useNodeEndpoint: true,
      params: buildSearchParams(query, limit),
    },
  );
  return data;
}
